import {WorkstreamAccess} from 'client';
import {parseAndExtractTextFromLexicalJson} from 'components/DSYS/editor/utils/lexical';
import {useEffect, useLayoutEffect, useState} from 'react';

export const useTableCellWidth = ({
  ref,
  trigger,
  enabled = true,
}: {
  ref: React.RefObject<HTMLDivElement>;
  trigger: any;
  /** When false, skips measuring (e.g. parent passes unconstrained chip width). */
  enabled?: boolean;
}) => {
  const [cellWidth, setCellWidth] = useState(0);

  useLayoutEffect(() => {
    if (!enabled) {
      return;
    }

    const cell = ref.current?.closest('td') as HTMLElement | null;
    const container = ref.current;
    if (!cell || !container) return;

    let raf = 0;
    const measure = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const prevMin = container.style.maxWidth;
        container.style.maxWidth = '';
        const next = Math.round(cell.getBoundingClientRect().width);
        container.style.maxWidth = prevMin;
        setCellWidth((w) => (w !== next ? next : w));
      });
    };

    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(cell);

    const mo = new MutationObserver(measure);
    mo.observe(container, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
    });

    const onTransitionEnd = () => measure();
    container.addEventListener('transitionend', onTransitionEnd, true);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      mo.disconnect();
      container.removeEventListener('transitionend', onTransitionEnd, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger, enabled]);

  return {cellWidth, setCellWidth};
};

export const useSyncWorkstreamNameWithEditorState = ({
  workstream,
  initialContentEditorState,
  handleContentEditorStateChange,
}: {
  workstream: WorkstreamAccess;
  initialContentEditorState: string | undefined;
  handleContentEditorStateChange: (
    workstreamId: number,
    contentEditorState: string | undefined,
    sourceText?: string
  ) => void;
}) => {
  const initialContentEditorStateText = initialContentEditorState
    ? parseAndExtractTextFromLexicalJson(initialContentEditorState)
    : null;

  useEffect(() => {
    const workstreamName = workstream.name.trim();
    if (initialContentEditorStateText && initialContentEditorStateText !== workstreamName) {
      handleContentEditorStateChange?.(workstream.id, workstreamName, workstreamName);
    }
  }, [
    initialContentEditorStateText,
    workstream.id,
    workstream.name,
    handleContentEditorStateChange,
  ]);
};
